<template>
  <Card>
    <template #content>
      <h2>{{ 
        props.selectedSite 
          ? `Monitoring Workflows for ${props.selectedSite.name}` 
          : 'Monitoring Workflows'
      }}</h2>
      <Tabs value="0">
        <TabList>
          <Tab value="0">Monitoring Workflows</Tab>
          <Tab value="1">Pipeline Overview</Tab>
        </TabList>
        <TabPanels>
          <TabPanel value="0">
            <p>{{ 
              props.siteIdFilter || props.selectedSite 
                ? 'View monitoring workflows for this site' 
                : 'View monitoring workflows across all sites'
            }}</p>
            <div class="refresh-row">
              <Button
                label="Refresh"
                icon="pi pi-refresh"
                size="small"
                :loading="isLoading"
                :disabled="isLoading"
                @click="fetchActiveProcesses"
              />
              <InputText
                v-model="searchQuery"
                type="text"
                placeholder="Search workflows"
                class="workflow-search"
              />
              <router-link :to="{ name: 'ProcessingDebug' }" class="debug-link">
                Open Processing Debug
              </router-link>
              <router-link :to="{ name: 'Command Control Panel' }" class="debug-link">
                Open Command Control
              </router-link>
            </div>
            <DataTable 
              :value="activeProcesses" 
              :paginator="true" 
              :rows="10"
              :sortField="'status'"
              :sortOrder="1"
              :key="sortKey"
            >
              <Column field="name" header="Workflow">
                <template #body="slotProps">
                  <router-link 
                    :to="{ name: 'ProjectDetail', params: { id: slotProps.data.id } }"
                    class="project-link"
                    v-tooltip.top="slotProps.data.description || 'No description available'"
                  >
                    {{ slotProps.data.name }}
                  </router-link>
                </template>
              </Column>
              <Column field="status" header="Status" sortable></Column>
              <Column 
                v-if="!props.selectedSite && !props.siteIdFilter" 
                field="area_of_interest_name" 
                header="Site"
              ></Column>
              <Column field="parameters" header="Configuration">
                <template #body="slotProps">
                  <div class="config-range" v-if="slotProps.data.parameters">
                    <div v-if="slotProps.data.parameters.start_date">
                      {{ formatDisplayDate(slotProps.data.parameters.start_date) }}
                    </div>
                    <div
                      class="config-range-separator"
                      v-if="slotProps.data.parameters.start_date && slotProps.data.parameters.end_date"
                    >
                      - to -
                    </div>
                    <div v-if="slotProps.data.parameters.end_date">
                      {{ formatDisplayDate(slotProps.data.parameters.end_date) }}
                    </div>
                    <div class="config-ongoing" v-if="isOngoing(slotProps.data.parameters)">
                      [ongoing]
                    </div>
                  </div>
                </template>
              </Column>
            </DataTable>
          </TabPanel>
          <TabPanel value="1">
            <WorkflowPipelineView />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </template>
  </Card>

</template>

<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted, watch } from 'vue'
import { Card, DataTable, Column, Button, InputText } from 'primevue'
import type { Site } from '@/composables/useSites'
import WorkflowPipelineView from '@/views/WorkflowPipelineView.vue'

export interface ActiveProcessingProps {
  selectedSite?: Site | null
  siteIdFilter?: number | number[] | null | undefined
  active?: boolean
}

const props = defineProps<ActiveProcessingProps>()

const allActiveProcesses = ref<any[]>([])
const searchQuery = ref('')
const isPolling = ref(false)
const isFetching = ref(false)
let stopRefreshCycleRequested = false
const isLoading = computed(() => isFetching.value || isPolling.value)

// Computed property to filter processes based on siteIdFilter or selectedSite
const activeProcesses = computed(() => {
  let filterIds: number[] = []
  const normalizedQuery = searchQuery.value.trim().toLowerCase()

  const filterByQuery = (processes: any[]) => {
    if (!normalizedQuery) {
      return processes
    }

    return processes.filter(process =>
      String(process.name || '').toLowerCase().includes(normalizedQuery)
    )
  }
  
  // Determine what site IDs to filter by
  if (props.siteIdFilter) {
    if (Array.isArray(props.siteIdFilter)) {
      if (props.siteIdFilter.length === 0) {
        return sortProcesses(filterByQuery(allActiveProcesses.value)) // Empty array means no filter
      }
      filterIds = props.siteIdFilter
    } else {
      filterIds = [props.siteIdFilter] // Single ID
    }
  } else if (props.selectedSite?.id) {
    // Backward compatibility with selectedSite
    filterIds = [props.selectedSite.id]
  }
  
  // If no filter criteria, return all processes (sorted)
  if (filterIds.length === 0) {
    return sortProcesses(filterByQuery(allActiveProcesses.value))
  }
  
  // Filter by area_of_interest matching the site IDs, then sort
  const filtered = allActiveProcesses.value.filter(process => 
    filterIds.includes(process.area_of_interest)
  )

  return sortProcesses(filterByQuery(filtered))
})

// Computed property to create unique sort key for forcing DOM updates
const sortKey = computed(() => {
  // Create a unique key based on the sorted order of process IDs and priorities
  const processes = activeProcesses.value
  return processes.map(p => `${p.id}-${p.priority}`).join(',')
})

// Sort processes: active first (priority === 1), then by date created (oldest first)
const sortProcesses = (processes: any[]) => {
  return [...processes].sort((a, b) => {
    // First sort by active status (priority === 1 comes first)
    const aActive = a.priority === 1 ? 1 : 0
    const bActive = b.priority === 1 ? 1 : 0
    
    if (aActive !== bActive) {
      return bActive - aActive // Active (1) before inactive (0)
    }
    
    // If both have same active status, sort by created date (oldest first)
    const aDate = new Date(a.created || a.date_created || 0)
    const bDate = new Date(b.created || b.date_created || 0)
    return aDate.getTime() - bDate.getTime() // Oldest first
  })
}

const getOrdinalDay = (day: number) => {
  const j = day % 10
  const k = day % 100

  if (j === 1 && k !== 11) return `${day}st`
  if (j === 2 && k !== 12) return `${day}nd`
  if (j === 3 && k !== 13) return `${day}rd`
  return `${day}th`
}

const formatDisplayDate = (value: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]

  const day = getOrdinalDay(date.getUTCDate())
  const month = months[date.getUTCMonth()]
  const year = date.getUTCFullYear()

  return `${day} ${month} ${year}`
}

const isOngoing = (parameters: Record<string, any>) => {
  const value =
    parameters.ongoing_monitoring ??
    parameters.ongoing ??
    parameters.is_ongoing

  return value === true || value === 'true' || value === 'yes' || value === 'Yes'
}

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const startPollingWindow = async () => {
  if (isPolling.value) {
    return
  }

  isPolling.value = true
  stopRefreshCycleRequested = false

  try {
    for (let attempt = 1; attempt <= 10; attempt += 1) {
      if (stopRefreshCycleRequested) {
        break
      }

      await fetchActiveProcesses()

      if (attempt < 10) {
        await sleep(3000)
      }
    }
  } finally {
    isPolling.value = false
  }
}


const fetchActiveProcesses = async () => {
  if (isFetching.value) {
    return
  }

  isFetching.value = true
  console.log('Fetch active processes is TRIGGERED')
  
  try {
    // Always fetch all projects and filter reactively
    const response = await fetch('/api/projects/')
    if (!response.ok) {
      const errorText = await response.text() // Get detailed error message
      console.error('Failed to fetch active processes:', errorText)
      throw new Error(`Failed to fetch active processes: ${errorText}`)
    }

    const data = await response.json()
    console.log('Active processes:', data)
    allActiveProcesses.value = data // Store all processes
    
  } catch (error: any) {
    console.error('Error fetching active processes:', error?.message || error)
  } finally {
    isFetching.value = false
  }
}

// Expose the fetch function so parent can refresh the data
defineExpose({
  refreshActiveProcesses: fetchActiveProcesses,
  startPollingWindow
})

onMounted(async () => {
  await fetchActiveProcesses()
})

watch(
  () => props.active,
  (next) => {
    const shouldPoll = next ?? true

    if (shouldPoll) {
      void startPollingWindow()
    } else {
      stopRefreshCycleRequested = true
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  stopRefreshCycleRequested = true
})
</script>

<style scoped>
/* Add any component-specific styles here */
.process-name-with-tooltip {
  cursor: help;
  border-bottom: 1px dotted #999;
  text-decoration: none;
}

.process-name-with-tooltip:hover {
  border-bottom-color: var(--primary-color, #3b82f6);
}

.project-link {
  color: var(--primary-color, #3b82f6);
  text-decoration: none;
  border-bottom: 1px dotted var(--primary-color, #3b82f6);
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.project-link:hover {
  color: var(--primary-color-dark, #2563eb);
  border-bottom-style: solid;
  border-bottom-width: 2px;
}

.config-range {
  line-height: 1.3;
}

.config-range-separator {
  opacity: 0.75;
}

.config-ongoing {
  margin-top: 0.25rem;
  font-weight: 600;
}

.refresh-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.workflow-search {
  min-width: 14rem;
}

.debug-link {
  color: var(--primary-color, #3b82f6);
  text-decoration: none;
  font-weight: 500;
}

.debug-link:hover {
  text-decoration: underline;
}
</style>
